import React, { useEffect, useState } from 'react'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import {
  Box,
  Button,
  Card,
  CardActionArea,
  Stack,
  Step,
  StepContent,
  StepLabel,
  Stepper,
  TextField,
  Typography
} from '@mui/material'
import CircularProgress from '@mui/material/CircularProgress'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { StaticDatePicker } from '@mui/x-date-pickers/StaticDatePicker'
import circlesOrbitColorPalette from 'common/ui/circlesColorTheme'
import addBusinessDays from 'date-fns/addBusinessDays'
import isWeekend from 'date-fns/isWeekend'

interface BookingDateTimeProps {
  formattedDeliveryDate: string
  setDeliveryDate: (value: Date | number | null) => void
  setNextPage: (value: void) => void
  setDeliveryTime: (value: string) => void
  deliveryTime: string
}

function DeliveryDateTime({
  formattedDeliveryDate,
  setDeliveryDate,
  setNextPage,
  setDeliveryTime,
  deliveryTime
}: BookingDateTimeProps) {
  const [date, setDate] = useState<Date | number | null>()
  const [activeStep, setActiveStep] = useState(0)
  const [minimumDate, setMinimumDate] = useState<Date | number | null>()

  const goNextStep = () => {
    setActiveStep((prevStep) => (prevStep += 1))
  }

  const goPreviousStep = () => {
    setActiveStep((prevStep) => (prevStep -= 1))
  }

  useEffect(() => {
    const TODAY_DATE = new Date()

    const MIN_DATE = addBusinessDays(TODAY_DATE, 3)

    setMinimumDate(MIN_DATE)
  }, [])

  /*first step - to select date  */
  const BookingCalendar = () => {
    return (
      <>
        {minimumDate ? (
          <Stack
            direction='row'
            sx={{
              '& .MuiPickersDay-today': {
                backgroundColor: 'neutrals.200',
                border: '0px'
              }
            }}
          >
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <StaticDatePicker
                displayStaticWrapperAs='desktop'
                openTo='day'
                value={date}
                minDate={minimumDate}
                renderInput={(params) => <TextField {...params} fullWidth />}
                shouldDisableDate={isWeekend}
                onChange={(_date) => {
                  setDate(_date)
                  setDeliveryDate(_date || null)
                }}
                showToolbar
              />
            </LocalizationProvider>
          </Stack>
        ) : (
          <CircularProgress />
        )}
        <Stack direction='row' sx={{ justifyContent: 'flex-start' }}>
          <Button
            onClick={() => goNextStep()}
            sx={{ border: 'none' }}
            endIcon={<ChevronRightIcon />}
          >
            Next Step
          </Button>
        </Stack>
      </>
    )
  }
  /*second step - to select time  */

  const BookingTime = () => {
    const timeSlots = [
      {
        timingDay: 'AM',
        timeRangeText: '10:00AM - 05:00PM'
      }
    ]
    const handleBookingTime = (timeRange: string) => {
      setDeliveryTime(timeRange)
    }

    return (
      <>
        {timeSlots.map((slot, index) => (
          <Card
            key={`slot-${index}`}
            sx={{
              width: '20%',
              borderRadius: '8px',
              margin: '16px 0',
              minWidth: '200px'
            }}
          >
            <CardActionArea
              sx={{
                textAlign: 'center',
                border: `2px solid ${circlesOrbitColorPalette.primary.main}`,
                borderRadius: '8px',
                ...(slot.timeRangeText === deliveryTime && {
                  backgroundColor: '#E7F5FF',
                  border: '2px solid #3DA9FC'
                })
              }}
              onClick={() => handleBookingTime(slot.timeRangeText)}
            >
              <Typography>{slot.timeRangeText}</Typography>
            </CardActionArea>
          </Card>
        ))}
        <Stack direction='row' sx={{ justifyContent: 'flex-start' }}>
          <Button
            onClick={() => goPreviousStep()}
            sx={{
              border: 'none',
              paddingLeft: '0.5vw',
              margin: '0'
            }}
            startIcon={<ChevronLeftIcon />}
          >
            Previous Step
          </Button>
        </Stack>
      </>
    )
  }

  const bookingSteps = [
    {
      label: 'Select Available Time Slots for Delivery',
      component: <BookingCalendar />
    },
    // { label: 'Select Preferred Delivery Method' },
    { label: 'Select Available Time', component: <BookingTime /> }
  ]

  return (
    <>
      <Typography variant='h4'>Booking</Typography>
      <Typography variant='subtitle1'>
        Select the date and time available
      </Typography>
      <Stack>
        <Box sx={{ display: 'flex', height: '60vh' }}>
          <Box sx={{ margin: '1vh', width: '60%' }}>
            <Box>
              <Stepper orientation='vertical' activeStep={activeStep}>
                {bookingSteps.map((step, index) => (
                  <Step key={`bookDelivery-${index}`}>
                    <StepLabel
                      sx={{
                        '& .MuiStepIcon-text': {
                          display: 'none'
                        },
                        '& circle': {
                          r: '8px'
                        },
                        '& .MuiStepLabel-label.Mui-active': {
                          color: circlesOrbitColorPalette.primary.main
                        }
                      }}
                    >
                      {step.label}
                    </StepLabel>
                    <StepContent>
                      {bookingSteps[activeStep].component}
                    </StepContent>
                  </Step>
                ))}
              </Stepper>
            </Box>
          </Box>

          <Box>
            <Card sx={{ padding: '5vh' }}>
              <Typography variant='h4'>Delivery Details: </Typography>
              <Typography variant='h6'>Delivery Date:</Typography>
              <Typography>{formattedDeliveryDate}</Typography>
              <Typography variant='h6'>Delivery Time:</Typography>
              <Typography>
                {!deliveryTime
                  ? `Delivery time slot not selected`
                  : deliveryTime}
              </Typography>
            </Card>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant='filled'
            disabled={!deliveryTime || !date}
            onClick={() => setNextPage()}
          >
            Next
          </Button>
        </Box>
      </Stack>
    </>
  )
}

export default DeliveryDateTime
